/**
 * timekeeping — the clock itself.
 *
 * The API for sessions, punches and time-adjustment requests has existed since
 * Task 5; until now nothing in Admin Web could read it, so the whole Time Clock
 * area was invisible to an administrator. This module is that contract.
 *
 * Reads need `time:read`. Requesting a correction needs `time:adjust`; DECIDING
 * one needs `timekeeping:manage`. Clock in/out is deliberately absent: it is the
 * worker's action and belongs to the mobile app. An administrator corrects a
 * record through an adjustment, which leaves a reviewable trail, never by
 * forging a punch.
 */
import { get, post } from './transport.js';
// --- reads ------------------------------------------------------------------
/** `GET /timekeeping/workers/:workerId/current` — the open session, if any. */
export async function currentSession(workerId, options) {
    const body = await get(`/timekeeping/workers/${workerId}/current`, options);
    return body.session ?? null;
}
/** `GET /timekeeping/workers/:workerId/sessions` — a worker's sessions since an instant. */
export async function workerSessions(workerId, query = {}, options) {
    const body = await get(`/timekeeping/workers/${workerId}/sessions`, {
        ...options,
        query: {
            since: query.since,
            includeSuperseded: query.includeSuperseded ? 'true' : undefined,
        },
    });
    return body.sessions;
}
/** `GET /timekeeping/sessions/:id/punches` — the clock events inside one session. */
export async function sessionPunches(sessionId, options) {
    const body = await get(`/timekeeping/sessions/${sessionId}/punches`, options);
    return body.punches;
}
/** `GET /timekeeping/adjustments` — the correction queue. */
export async function adjustments(query = {}, options) {
    const body = await get('/timekeeping/adjustments', {
        ...options,
        query: { ...query },
    });
    return body.requests;
}
/**
 * `POST /timekeeping/adjustments/:id/decision` — approve or deny a correction.
 *
 * NEEDS `timekeeping:manage`, AND THAT KEY ALONE IS NOT ENOUGH. The route
 * (apps/api/src/domains/timekeeping/routes.ts:587) applies three gates in order,
 * and a caller can pass the first and fail either of the others:
 *
 *   1. `timekeeping:manage` — the door.
 *   2. `requireOwnWorkerToWrite(principal, req.workerId)` (routes.ts:659) —
 *      whose time you may CHANGE: your own worker, a worker on your
 *      `worker.supervisor_id` edge, or anybody with `timekeeping:write_all`
 *      (org_admin only). The seeded `supervisor` role holds
 *      `timekeeping:manage` organization-wide, so this is the gate that stops a
 *      supervisor assigned to nobody from approving a stranger's claim.
 *      Approving IS the write — it inserts a replacement session and punches.
 *   3. Separation of duties (routes.ts:684) — the SUBJECT worker's linked user
 *      may not decide, on EITHER branch. Denying your own correction is deciding
 *      your own case just as much as approving it.
 *
 * Each of 2 and 3 is a 403 with its own sentence. A caller must render them as
 * refusals, never as a retryable failure: asking again cannot change who you
 * supervise or who you are.
 */
export async function decideAdjustment(id, input, options) {
    const body = await post(`/timekeeping/adjustments/${id}/decision`, input, options);
    return body.request;
}
/**
 * `POST /timekeeping/adjustments/:id/cancel` — withdraw a correction you filed.
 *
 * NEEDS `time:adjust` — the key that FILES one — and, on top of it, that the
 * caller BE the requester. That second condition is not derivable from a
 * permission set: it is `requested_by_user_id` on the row against the caller's
 * own `userId`, and the server checks it inside the transaction under the row
 * lock, so a client that offers the control to somebody else meets a 403 rather
 * than getting away with it.
 *
 * DELIBERATELY NOT `timekeeping:manage`. Withdrawing is not a decision — a
 * reviewer who wants a correction gone has `deny`, which is attributed and
 * answerable. "Withdrawing" somebody else's request would be a denial with the
 * paperwork removed.
 *
 * REFUSALS: 403 when it is not yours, 409 when it is no longer `pending` (a
 * decided correction cannot be un-decided), 404 when it does not exist in this
 * organization. Only the 409 is worth a retry, and only after a refresh.
 *
 * This writes the `cancelled` status, which `tar_status_ck` has allowed since
 * 1755064800000 and which no route could reach until this one — so `Withdrawn`
 * was a word both clients rendered for a state no record could hold.
 */
export async function cancelAdjustment(id, options) {
    const body = await post(`/timekeeping/adjustments/${id}/cancel`, undefined, options);
    return body.request;
}
/**
 * `POST /timekeeping/adjustments` — ask for a correction to the record.
 *
 * NEEDS `time:adjust`, PLUS REACH OVER `workerId`. The route (routes.ts:510)
 * runs the SAME `requireOwnWorkerToWrite` the decision does, and its comment
 * states the rule: "A time:adjust holder may request a correction to their own
 * timekeeping or that of a worker assigned to them. Filing one about anybody
 * else is a claim about that person's hours and takes `timekeeping:write_all`".
 *
 * SO AN OFFICE USER MAY FILE ON A WORKER'S BEHALF. `resolveSubjectUserId`
 * (routes.ts:295) says so in as many words — "The subject is NOT the requester:
 * an admin or supervisor may file a correction on a worker's behalf, so
 * `requested_by_user_id` says nothing about whose time is being changed" — and
 * the decision route's own separation-of-duties guard is built around that being
 * the ordinary case. An org_admin holds `timekeeping:write_all` and reaches the
 * organization; a supervisor reaches the workers assigned to them.
 *
 * THE PER-TYPE SHAPE IS ENFORCED, not advisory. `createAdjustmentSchema`'s
 * superRefine (schemas.ts:56) requires: `add_missing_session` →
 * `requestedClockInAt`; `edit_times` → `timeSessionId` and at least one of the
 * two instants; `add_missing_punch` → `timeSessionId` and at least one instant;
 * `void_session` → `timeSessionId`. And `requestedClockOutAt` may not precede
 * `requestedClockInAt`. Both instants are `z.string().datetime({ offset: true })`
 * — an ISO string carrying `Z` or `±HH:MM`, which is what `Date#toISOString`
 * produces and what a bare `<input type="datetime-local">` value does NOT.
 */
export async function requestAdjustment(input, options) {
    const body = await post('/timekeeping/adjustments', input, options);
    return body.request;
}
//# sourceMappingURL=timekeeping.js.map